import { Show, useRecordContext, useGetList, EditButton, useDataProvider, useNotify, useRefresh } from 'react-admin';
import {
  Box, Typography, Chip, Stack, Card, CardContent,
  Table, TableHead, TableRow, TableCell, TableBody,
  Divider, IconButton, Tooltip, Collapse, TextField,
  Select, MenuItem, FormControl, InputLabel, Button,
  CircularProgress, Autocomplete,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon      from '@mui/icons-material/Clear';
import BuildIcon    from '@mui/icons-material/Build';
import EditIcon      from '@mui/icons-material/Edit';
import DeleteIcon    from '@mui/icons-material/Delete';
import ArchiveIcon   from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import AddIcon       from '@mui/icons-material/Add';
import CloseIcon     from '@mui/icons-material/Close';
import SaveIcon      from '@mui/icons-material/Save';
import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import authFetch, { responseError } from '../utils/authFetch';
import { fmtQty } from '../styles/ds';
import SupplierInventoryBreakdown from '../components/SupplierInventoryBreakdown';

// ── helpers ──────────────────────────────────────────────────────────────────

const parseOptions = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options.map(o => String(o).trim());
  if (typeof options === 'string') return options.split(',').map(o => o.trim()).filter(Boolean);
  return [];
};

const StockBadge = ({ qty, unit }) => {
  const color = qty === 0 ? '#721C24' : qty < 3 ? '#856404' : '#155724';
  const bg    = qty === 0 ? '#F8D7DA' : qty < 3 ? '#FFF3CD' : '#D4EDDA';
  return (
    <span style={{ backgroundColor: bg, color, padding: '2px 10px', borderRadius: 12, fontWeight: 600, fontSize: 12 }}>
      {qty === 0 ? 'אזל' : fmtQty(qty, unit)}
    </span>
  );
};

const AttributeChips = ({ attrs, attrDefs = [] }) => {
  if (!attrs || typeof attrs !== 'object') return <span style={{ color: '#aaa' }}>—</span>;
  // When attrDefs is provided, show only category-linked attributes in definition order
  const entries = attrDefs.length > 0
    ? attrDefs.flatMap(a => attrs[a.key] != null && attrs[a.key] !== ''
        ? [{ key: a.key, label: a.label || a.key, value: attrs[a.key] }]
        : [])
    : Object.entries(attrs).map(([k, v]) => ({ key: k, label: k, value: v }));

  if (entries.length === 0) return <span style={{ color: '#aaa' }}>—</span>;
  return (
    <Stack className="crm-wrap" direction="row" spacing={0.5} flexWrap="wrap">
      {entries.map(({ key, label, value }) => (
        <Chip key={key} label={`${label}: ${value}`} size="small"
          sx={{ backgroundColor: 'rgba(13,29,62,0.08)', color: '#0d1d3e', fontSize: 11 }} />
      ))}
    </Stack>
  );
};

const fieldSx = {
  backgroundColor: '#fff',
  borderRadius: 1,
  '& .MuiOutlinedInput-root': { borderRadius: 1 },
};

// ── shared attribute grid ─────────────────────────────────────────────────────

const AttributeFields = ({ productId, attrs, setAttrs }) => {
  const { data: attributes = [], isLoading } = useGetList('attributes', {
    filter: { product_id: productId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'label', order: 'ASC' },
  });

  if (isLoading) return <CircularProgress size={18} />;
  if (!attributes.length) return null;

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        תכונות המוצר
      </Typography>
      <Box className="crm-form-grid" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
        {attributes.map(attr => {
          const opts = parseOptions(attr.options);
          return opts.length > 0 ? (
            <FormControl key={attr.id} fullWidth>
              <InputLabel sx={{ backgroundColor: '#fff', px: 0.5 }}>{attr.label || attr.key}</InputLabel>
              <Select
                label={attr.label || attr.key}
                value={attrs[attr.key] || ''}
                onChange={e => setAttrs(a => ({ ...a, [attr.key]: e.target.value }))}
                sx={fieldSx}
              >
                <MenuItem value=""><em>בחר...</em></MenuItem>
                {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <TextField
              key={attr.id}
              label={attr.label || attr.key}
              value={attrs[attr.key] || ''}
              onChange={e => setAttrs(a => ({ ...a, [attr.key]: e.target.value }))}
              fullWidth sx={fieldSx}
            />
          );
        })}
      </Box>
    </>
  );
};

// ── inline add form ───────────────────────────────────────────────────────────

const EMPTY_FORM = { name: '', price_ils: '', price_usd: '', quantity: 0 };

const InlineVariantForm = ({ productId, onClose, onSaved }) => {
  const dataProvider = useDataProvider();
  const notify       = useNotify();
  const [fields, setFields] = useState(EMPTY_FORM);
  const [attrs, setAttrs]   = useState({});
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setFields(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!fields.name.trim()) { notify('נא להזין שם מוצר', { type: 'warning' }); return; }
    setSaving(true);
    try {
      await dataProvider.create('variants', {
        data: {
          product_id: productId,
          name:       fields.name.trim(),
          price_ils:  Number(fields.price_ils) || 0,
          price_usd:  Number(fields.price_usd) || 0,
          quantity:   Number(fields.quantity)  || 0,
          attributes: attrs,
        },
      });
      notify('המוצר נוסף בהצלחה', { type: 'success' });
      setFields(EMPTY_FORM);
      setAttrs({});
      onSaved();
    } catch (e) {
      console.error('Create variant error:', e);
      notify(e?.body?.error || 'שגיאה בשמירה — בדוק שהשרת פועל', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 2, border: '1.5px solid rgba(200,169,110,0.5)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <CardContent className="crm-card-content" sx={{ p: 3 }}>
        <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography fontWeight={700} fontSize={16} color="#0d1d3e">הוספת מוצר חדש</Typography>
          <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <TextField
          label="שם המוצר (למשל: תפילין גסות אשכנזי גודל 7)"
          value={fields.name}
          onChange={e => set('name', e.target.value)}
          fullWidth sx={{ mb: 3, ...fieldSx }}
          inputProps={{ dir: 'rtl' }}
        />

        <AttributeFields productId={productId} attrs={attrs} setAttrs={setAttrs} />

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>מחיר ומלאי</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <TextField label="מחיר ₪" type="number" value={fields.price_ils}
            onChange={e => set('price_ils', e.target.value)} fullWidth sx={fieldSx}
            InputProps={{ inputProps: { min: 0 } }} />
          <TextField label="מחיר $" type="number" value={fields.price_usd}
            onChange={e => set('price_usd', e.target.value)} fullWidth sx={fieldSx}
            InputProps={{ inputProps: { min: 0 } }} />
          <TextField label="כמות במלאי" type="number" value={fields.quantity}
            onChange={e => set('quantity', e.target.value)} fullWidth sx={fieldSx}
            InputProps={{ inputProps: { min: 0 } }} />
        </Box>

        <Stack className="crm-actions" direction="row" spacing={1.5} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}
            sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>ביטול</Button>
          <Button variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ backgroundColor: '#0d1d3e', borderRadius: 2, px: 3, '&:hover': { backgroundColor: '#162d5a' } }}>
            שמור
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ── inline edit form ──────────────────────────────────────────────────────────

const InlineVariantEditForm = ({ variant, productId, onClose, onSaved }) => {
  const dataProvider = useDataProvider();
  const notify       = useNotify();
  const [fields, setFields] = useState({
    name:      variant.name      || '',
    price_ils: variant.price_ils ?? '',
    price_usd: variant.price_usd ?? '',
  });
  const [attrs, setAttrs] = useState(
    variant.attributes && typeof variant.attributes === 'object' ? { ...variant.attributes } : {}
  );
  const [saving, setSaving] = useState(false);

  // Opening-balance editor — the only part of this variant's stock that can
  // safely be changed here (scoped server-side to batch_type IN ('opening',
  // 'adjustment')). Prefilled from the same per-source breakdown the
  // read-only <SupplierInventoryBreakdown> below shows, so the two numbers
  // never disagree.
  const [opening, setOpening] = useState(null); // { quantity, unit_cost_ils, unit_cost_usd }
  const [openingSaving, setOpeningSaving] = useState(false);

  useEffect(() => {
    authFetch(`/variants/${variant.id}/supplier-inventory`)
      .then(r => r.json())
      .then(d => {
        const rows = (d.rows || []).filter(r => r.source_type === 'opening' || r.source_type === 'adjustment');
        const quantity = rows.reduce((s, r) => s + Number(r.available_qty || 0), 0);
        const costedRow = rows.find(r => r.unit_cost_ils != null) || rows.find(r => r.unit_cost_usd != null);
        setOpening({
          quantity,
          unit_cost_ils: costedRow?.unit_cost_ils ?? '',
          unit_cost_usd: costedRow?.unit_cost_usd ?? '',
        });
      })
      .catch(() => setOpening({ quantity: 0, unit_cost_ils: '', unit_cost_usd: '' }));
  }, [variant.id]);

  const set = (key, val) => setFields(f => ({ ...f, [key]: val }));
  const setOpeningField = (key, val) => setOpening(o => ({ ...o, [key]: val }));

  const handleSave = async () => {
    if (!fields.name.trim()) { notify('נא להזין שם מוצר', { type: 'warning' }); return; }
    setSaving(true);
    try {
      await dataProvider.update('variants', {
        id: variant.id,
        data: {
          product_id: productId,
          name:       fields.name.trim(),
          price_ils:  Number(fields.price_ils) || 0,
          price_usd:  Number(fields.price_usd) || 0,
          attributes: attrs,
        },
        previousData: variant,
      });
      notify('המוצר עודכן בהצלחה', { type: 'success' });
      onSaved();
    } catch (e) {
      console.error('Update variant error:', e);
      notify(e?.body?.error || 'שגיאה בעדכון — בדוק שהשרת פועל', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOpening = async () => {
    setOpeningSaving(true);
    try {
      const res = await authFetch(`/variants/${variant.id}/opening-balance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity:      Number(opening.quantity) || 0,
          unit_cost_ils: opening.unit_cost_ils === '' ? null : Number(opening.unit_cost_ils),
          unit_cost_usd: opening.unit_cost_usd === '' ? null : Number(opening.unit_cost_usd),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'שגיאה בעדכון יתרת פתיחה');
      }
      notify('יתרת הפתיחה עודכנה בהצלחה', { type: 'success' });
      onSaved();
    } catch (e) {
      notify(e.message, { type: 'error' });
    } finally {
      setOpeningSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 2, border: '1.5px solid rgba(13,29,62,0.3)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <CardContent className="crm-card-content" sx={{ p: 3 }}>
        <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography fontWeight={700} fontSize={16} color="#0d1d3e">עריכת מוצר</Typography>
          <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <TextField
          label="שם המוצר"
          value={fields.name}
          onChange={e => set('name', e.target.value)}
          fullWidth sx={{ mb: 3, ...fieldSx }}
          inputProps={{ dir: 'rtl' }}
        />

        <AttributeFields productId={productId} attrs={attrs} setAttrs={setAttrs} />

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>מחיר מכירה (גלובלי למוצר)</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <TextField label="מחיר ₪" type="number" value={fields.price_ils}
            onChange={e => set('price_ils', e.target.value)} fullWidth sx={fieldSx}
            InputProps={{ inputProps: { min: 0 } }} />
          <TextField label="מחיר $" type="number" value={fields.price_usd}
            onChange={e => set('price_usd', e.target.value)} fullWidth sx={fieldSx}
            InputProps={{ inputProps: { min: 0 } }} />
        </Box>

        <Stack className="crm-actions" direction="row" spacing={1.5} justifyContent="flex-end" mb={3}>
          <Button variant="outlined" onClick={onClose}
            sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>ביטול</Button>
          <Button variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ backgroundColor: '#0d1d3e', borderRadius: 2, px: 3, '&:hover': { backgroundColor: '#162d5a' } }}>
            שמור שינויים
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Read-only — per-supplier price/quantity + the real total. Editing
            a specific supplier's quantity happens on its purchase record
            (link on each row), never here. */}
        <SupplierInventoryBreakdown variantId={variant.id} unit={variant.unit} />

        <Divider sx={{ my: 2 }} />

        {/* The only quantity actually editable from this screen — scoped
            server-side to batch_type IN ('opening','adjustment'), so it can
            never touch supplier- or production-sourced stock shown above. */}
        <Typography fontWeight={700} fontSize={13} color="#0d1d3e" mb={1}>יתרת פתיחה</Typography>
        {opening === null ? (
          <Typography fontSize={12} color="text.secondary">טוען...</Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 1.5 }}>
              <TextField label="כמות" type="number" value={opening.quantity}
                onChange={e => setOpeningField('quantity', e.target.value)} fullWidth sx={fieldSx}
                InputProps={{ inputProps: { min: 0 } }} />
              <TextField label="עלות ₪ ליחידה" type="number" value={opening.unit_cost_ils}
                onChange={e => setOpeningField('unit_cost_ils', e.target.value)} fullWidth sx={fieldSx}
                InputProps={{ inputProps: { min: 0 } }} />
              <TextField label="עלות $ ליחידה" type="number" value={opening.unit_cost_usd}
                onChange={e => setOpeningField('unit_cost_usd', e.target.value)} fullWidth sx={fieldSx}
                InputProps={{ inputProps: { min: 0 } }} />
            </Box>
            <Stack className="crm-actions" direction="row" justifyContent="flex-end">
              <Button variant="outlined"
                startIcon={openingSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                onClick={handleSaveOpening} disabled={openingSaving}
                sx={{ borderColor: '#0d1d3e', color: '#0d1d3e', borderRadius: 2, fontSize: 12 }}>
                שמור יתרת פתיחה
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── Production Costs panel per variant ───────────────────────────────────────

const COST_CURRENCY = [{ id: 'ILS', label: '₪ שקל' }, { id: 'USD', label: '$ דולר' }];

const VariantProductionCosts = ({ variantId }) => {
  const notify = useNotify();
  const [costs, setCosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [workers, setWorkers]       = useState([]);
  const [editingId, setEditingId]   = useState(null);
  const [addOpen, setAddOpen]       = useState(false);
  const [saving, setSaving]         = useState(false);

  // New-row state
  const [nWorker,   setNWorker]   = useState(null);
  const [nCost,     setNCost]     = useState('');
  const [nCurrency, setNCurrency] = useState('ILS');
  const [nDate,     setNDate]     = useState('');
  const [nNotes,    setNNotes]    = useState('');

  // Edit-row state
  const [eWorker,   setEWorker]   = useState(null);
  const [eCost,     setECost]     = useState('');
  const [eCurrency, setECurrency] = useState('ILS');
  const [eDate,     setEDate]     = useState('');
  const [eNotes,    setENotes]    = useState('');

  const loadCosts = useCallback(() => {
    setLoading(true);
    authFetch(`/production_costs?_start=0&_end=200&_sort=id&_order=ASC&filter=${encodeURIComponent(JSON.stringify({ variant_id: variantId }))}`)
      .then(r => r.json()).then(d => { setCosts(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [variantId]);

  useEffect(() => { loadCosts(); }, [loadCosts]);

  useEffect(() => {
    authFetch('/workers?_start=0&_end=500&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => setWorkers(Array.isArray(d) ? d : []));
  }, []);

  const workerOptions = workers.map(w => ({ id: w.id, label: w.name, role_name: w.role_name }));
  const fmt = (amount, cur) => cur === 'USD'
    ? `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : `₪${Number(amount || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;
  const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

  const openEdit = cost => {
    setAddOpen(false);
    setEditingId(cost.id);
    setEWorker(workerOptions.find(w => w.id === cost.worker_id) || null);
    setECost(cost.cost || '');
    setECurrency(cost.currency || 'ILS');
    setEDate(cost.work_date ? cost.work_date.slice(0, 10) : '');
    setENotes(cost.notes || '');
    // work_type is stored as string; show in notes or just reset
  };

  const handleAdd = async () => {
    if (!nCost || Number(nCost) <= 0) { notify('נא להזין עלות', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch('/production_costs', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant_id: variantId, worker_id: nWorker?.id || null, cost: Number(nCost), currency: nCurrency, work_date: nDate || null, notes: nNotes || null }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה'), { type: 'error' }); return; }
      notify('נוסף', { type: 'success' });
      setNWorker(null); setNCost(''); setNDate(''); setNNotes(''); setAddOpen(false);
      loadCosts();
    } catch { notify('שגיאה', { type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!eCost || Number(eCost) <= 0) { notify('נא להזין עלות', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch(`/production_costs/${editingId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant_id: variantId, worker_id: eWorker?.id || null, cost: Number(eCost), currency: eCurrency, work_date: eDate || null, notes: eNotes || null }),
      });
      if (!res.ok) { notify(await responseError(res, 'שגיאה'), { type: 'error' }); return; }
      notify('עודכן', { type: 'success' });
      setEditingId(null);
      loadCosts();
    } catch { notify('שגיאה', { type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('למחוק עלות ייצור זו?')) return;
    try {
      const res = await authFetch(`/production_costs/${id}`, { method: 'DELETE' });
      if (!res.ok) { notify(await responseError(res, 'שגיאה במחיקה'), { type: 'error' }); return; }
      notify('נמחק', { type: 'success' });
      loadCosts();
    } catch { notify('שגיאה במחיקה', { type: 'error' }); }
  };

  const rowSx = { backgroundColor: '#fff', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } };

  return (
    <Box mt={2} p={2} sx={{ backgroundColor: 'rgba(13,29,62,0.02)', borderRadius: 2, border: '1px solid rgba(13,29,62,0.08)' }}>
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography fontWeight={700} fontSize={13} color="#0d1d3e">עלויות ייצור</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />}
          onClick={() => { setAddOpen(o => !o); setEditingId(null); }}
          sx={{ fontSize: 11, borderRadius: 2, borderColor: '#c8a96e', color: '#c8a96e' }}>
          הוסף עלות
        </Button>
      </Stack>

      {/* Inline add form */}
      <Collapse in={addOpen} timeout={200} unmountOnExit>
        <Box className="crm-form-grid" sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto', gap: 1, mb: 1.5, p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px dashed rgba(200,169,110,0.5)' }}>
          <Autocomplete size="small" options={workerOptions} value={nWorker} onChange={(_, v) => setNWorker(v)}
            getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={p => <TextField {...p} placeholder="עובד" sx={rowSx} />} />
          <TextField size="small" type="number" placeholder="עלות *" value={nCost} onChange={e => setNCost(e.target.value)} sx={rowSx} />
          <FormControl size="small"><Select value={nCurrency} onChange={e => setNCurrency(e.target.value)} sx={rowSx}>{COST_CURRENCY.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}</Select></FormControl>
          <TextField size="small" type="date" value={nDate} onChange={e => setNDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={rowSx} />
          <TextField size="small" placeholder="הערות" value={nNotes} onChange={e => setNNotes(e.target.value)} sx={rowSx} />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={saving} sx={{ ...fieldSx, px: 1.5, backgroundColor: '#0d1d3e', '&:hover': { backgroundColor: '#162d5a' }, fontSize: 11, whiteSpace: 'nowrap' }}>
            {saving ? <CircularProgress size={12} color="inherit" /> : 'שמור'}
          </Button>
        </Box>
      </Collapse>

      {/* Costs table */}
      {loading ? <Typography fontSize={12} color="text.secondary">טוען...</Typography> :
      costs.length === 0 ? <Typography fontSize={12} color="text.secondary">אין עלויות ייצור לוריאנט זה</Typography> : (
        <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(13,29,62,0.06)' }}>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>עובד</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>תפקיד</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="right">עלות</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }} align="center">תאריך</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>הערות</TableCell>
              <TableCell sx={{ width: 72 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.map((pc, i) => editingId === pc.id ? (
              <TableRow key={pc.id} sx={{ backgroundColor: 'rgba(13,29,62,0.03)' }}>
                <TableCell colSpan={6} sx={{ py: 1 }}>
                  <Box className="crm-form-grid" sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto auto', gap: 1 }}>
                    <Autocomplete size="small" options={workerOptions} value={eWorker} onChange={(_, v) => setEWorker(v)}
                      getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={p => <TextField {...p} placeholder="עובד" sx={rowSx} />} />
                    <TextField size="small" type="number" value={eCost} onChange={e => setECost(e.target.value)} sx={rowSx} />
                    <FormControl size="small"><Select value={eCurrency} onChange={e => setECurrency(e.target.value)} sx={rowSx}>{COST_CURRENCY.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}</Select></FormControl>
                    <TextField size="small" type="date" value={eDate} onChange={e => setEDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={rowSx} />
                    <TextField size="small" value={eNotes} onChange={e => setENotes(e.target.value)} placeholder="הערות" sx={rowSx} />
                    <Button size="small" variant="contained" onClick={handleEdit} disabled={saving}
                      sx={{ backgroundColor: '#0d1d3e', '&:hover': { backgroundColor: '#162d5a' }, fontSize: 11 }}>שמור</Button>
                    <Button size="small" onClick={() => setEditingId(null)} sx={{ fontSize: 11, color: '#666' }}>ביטול</Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow key={pc.id} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : 'rgba(13,29,62,0.02)' }}>
                <TableCell sx={{ fontSize: 12 }}>{pc.worker_name || '—'}</TableCell>
                <TableCell>
                  {pc.role_name
                    ? <Chip label={pc.role_name} size="small" sx={{ backgroundColor: 'rgba(200,169,110,0.15)', color: '#7a6030', fontWeight: 600, fontSize: 10, height: 18 }} />
                    : <Typography fontSize={11} color="text.secondary">—</Typography>}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  {fmt(pc.total_cost ?? pc.cost, pc.currency)}
                  {pc.batch_quantity > 1 && (
                    <Typography component="div" fontSize={10} color="text.secondary" fontWeight={400}>
                      {fmt(pc.cost, pc.currency)} × {pc.batch_quantity} יח'
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtDate(pc.work_date)}</TableCell>
                <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{pc.notes || '—'}</TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <Stack direction="row" spacing={0.3}>
                    <IconButton size="small" onClick={() => openEdit(pc)} aria-label="עריכת עלות ייצור" sx={{ color: '#0d1d3e', opacity: 0.6 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(pc.id)} aria-label="מחיקת עלות ייצור" sx={{ color: '#c62828' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></Box>
      )}
    </Box>
  );
};

// ── main product detail ───────────────────────────────────────────────────────

const ProductDetail = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <ProductDetailContent record={record} />;
};

const ProductDetailContent = ({ record }) => {
  const notify = useNotify();
  const [formOpen, setFormOpen]             = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [costsVariantId, setCostsVariantId] = useState(null); // which variant's costs are open
  const [supplierVariantId, setSupplierVariantId] = useState(null); // which variant's supplier breakdown is open
  const [variants, setVariants]             = useState([]);
  const [isLoading, setIsLoading]           = useState(true);

  const loadVariants = useCallback(() => {
    setIsLoading(true);
    authFetch(`/variants?_start=0&_end=500&_sort=id&_order=ASC&filter=${encodeURIComponent(JSON.stringify({ product_id: record.id, include_archived: 'true' }))}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => { setVariants(Array.isArray(data) ? data : []); setIsLoading(false); })
      .catch(err => { console.error('Load variants error:', err); setIsLoading(false); });
  }, [record.id]);

  useEffect(() => { loadVariants(); }, [loadVariants]);

  const totalStock  = variants.reduce((sum, v) => sum + Number(v.quantity || 0), 0);
  const minPriceIls = variants.length ? Math.min(...variants.map(v => Number(v.price_ils))) : null;
  const minPriceUsd = variants.length ? Math.min(...variants.map(v => Number(v.price_usd))) : null;
  // Gross margin for variants that have cost_ils set
  const withCost = variants.filter(v => v.cost_ils != null && v.price_ils > 0);
  const avgMarginPct = withCost.length
    ? withCost.reduce((s, v) => s + (Number(v.price_ils) - Number(v.cost_ils)) / Number(v.price_ils), 0) / withCost.length * 100
    : null;

  const handleSaved   = () => { loadVariants(); setFormOpen(false); };
  const handleEdited  = () => { loadVariants(); setEditingVariant(null); };
  const handleDelete  = async (variant) => {
    if (!window.confirm(`למחוק לחלוטין את "${variant.name || 'וריאנט #' + variant.id}"?`)) return;
    try {
      const r    = await authFetch(`/variants/${variant.id}`, { method: 'DELETE' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (body.canArchive) {
          const doArchive = window.confirm(
            `${body.error}\n\nלארכב אותו? (יוסתר מהרשימה אך הנתונים ישמרו)`
          );
          if (doArchive) handleArchive(variant, true);
        } else {
          notify(body.error || 'שגיאה במחיקה', { type: 'error' });
        }
        return;
      }
      loadVariants();
    } catch {
      notify('שגיאה במחיקה — בדוק שהשרת פועל', { type: 'error' });
    }
  };

  const handleArchive = async (variant, skipConfirm = false) => {
    const name = variant.name || `וריאנט #${variant.id}`;
    if (!skipConfirm && !window.confirm(`לארכב את "${name}"? הוא יוסתר מהרשימה הפעילה.`)) return;
    try {
      const r = await authFetch(`/variants/${variant.id}/archive`, { method: 'PUT' });
      if (!r.ok) { notify(await responseError(r, 'שגיאה בארכוב'), { type: 'error' }); return; }
      notify(`"${name}" הועבר לארכיון`, { type: 'success' });
      loadVariants();
    } catch { notify('שגיאה בארכוב', { type: 'error' }); }
  };

  const handleUnarchive = async (variant) => {
    try {
      const r = await authFetch(`/variants/${variant.id}/unarchive`, { method: 'PUT' });
      if (!r.ok) { notify(await responseError(r, 'שגיאה בשחזור'), { type: 'error' }); return; }
      notify(`"${variant.name || 'וריאנט'}" שוחזר מהארכיון`, { type: 'success' });
      loadVariants();
    } catch { notify('שגיאה בשחזור', { type: 'error' }); }
  };

  const openAdd  = () => { setEditingVariant(null); setFormOpen(o => !o); };
  const openEdit = (variant) => { setFormOpen(false); setEditingVariant(v => v?.id === variant.id ? null : variant); };

  // ── Attribute filters ───────────────────────────────────────────────────────
  const [attrFilters, setAttrFilters] = useState({}); // { key: value | '' }

  // Load attribute definitions (with their declared options) for this product
  const { data: attrDefs = [] } = useGetList('attributes', {
    filter: { product_id: record.id },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'label', order: 'ASC' },
  });

  // key → Hebrew label map, built from linked attribute definitions
  const attrLabelMap = useMemo(() => {
    const map = {};
    attrDefs.forEach(attr => { map[attr.key] = attr.label || attr.key; });
    return map;
  }, [attrDefs]);

  // Build filter options only for attributes linked to this product category.
  // Seed from declared options, then add any extra values actually in variants —
  // but never introduce keys that aren't in attrDefs.
  const attrMeta = useMemo(() => {
    const meta = {}; // key → Set of values

    // 1. Seed from linked attribute definitions only
    attrDefs.forEach(attr => {
      const options = Array.isArray(attr.options)
        ? attr.options
        : typeof attr.options === 'string'
          ? attr.options.split(',').map(o => o.trim()).filter(Boolean)
          : [];
      meta[attr.key] = new Set(options.map(String));
    });

    // 2. Add values found in variants, but ONLY for keys already in meta
    variants.forEach(v => {
      Object.entries(v.attributes || {}).forEach(([k, val]) => {
        if (val == null || val === '' || !meta[k]) return;
        meta[k].add(String(val));
      });
    });

    // 3. Remove keys with no values (nothing to filter on)
    Object.keys(meta).forEach(k => { if (meta[k].size === 0) delete meta[k]; });

    return meta;
  }, [variants, attrDefs]);

  // Filtered variants — archived always visible at bottom, active filtered by attrFilters
  const filteredVariants = useMemo(() => {
    const active   = variants.filter(v => !v.archived_at);
    const archived = variants.filter(v => !!v.archived_at);
    const filtered = active.filter(v => {
      const attrs = v.attributes || {};
      return Object.entries(attrFilters).every(([k, val]) => {
        if (!val) return true;
        return String(attrs[k] ?? '') === val;
      });
    });
    return [...filtered, ...archived];
  }, [variants, attrFilters]);

  const hasFilters = Object.values(attrFilters).some(v => !!v);
  const clearFilters = () => setAttrFilters({});

  return (
    <Box className="crm-page crm-detail-content" p={3}>
      {/* Header */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#0d1d3e">{record.name}</Typography>
          {record.description && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>{record.description}</Typography>
          )}
        </Box>
        <EditButton label="ערוך קטגוריה" />
      </Stack>

      {/* Summary cards */}
      <Stack className="crm-wrap crm-stat-row" direction="row" spacing={2} mb={3} flexWrap="wrap">
        <Card sx={{ borderRadius: 2, minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">מוצרים בקטגוריה</Typography>
            <Typography fontWeight={700} fontSize={22} color="#0d1d3e">{variants.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, minWidth: 140 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">סה"כ במלאי</Typography>
            <Typography fontWeight={700} fontSize={22} color="#2e7d32">
              {fmtQty(totalStock, variants[0]?.unit)}
            </Typography>
          </CardContent>
        </Card>
        {minPriceIls !== null && (
          <Card sx={{ borderRadius: 2, minWidth: 160 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">מחיר מכירה (מינימום)</Typography>
              <Typography fontWeight={700} fontSize={16} color="#0d1d3e">
                ₪{minPriceIls.toLocaleString()} / ${minPriceUsd?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* ── Attribute filter bar ── */}
      {Object.keys(attrMeta).length > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(13,29,62,0.03)', borderRadius: 2, border: '1px solid rgba(13,29,62,0.08)' }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <FilterListIcon sx={{ fontSize: 16, color: '#0d1d3e', opacity: 0.6 }} />
            <Typography fontSize={13} fontWeight={700} color="#0d1d3e">סינון לפי תכונות</Typography>
            {hasFilters && (
              <Button size="small" startIcon={<ClearIcon sx={{ fontSize: 13 }} />}
                onClick={clearFilters}
                sx={{ fontSize: 11, color: '#c62828', ml: 1, p: 0.5 }}>
                נקה סינון ({variants.filter(v => !v.archived_at).length - filteredVariants.filter(v => !v.archived_at).length} מסוננים)
              </Button>
            )}
          </Stack>
          <Stack className="crm-wrap" direction="row" flexWrap="wrap" gap={1.5}>
            {Object.entries(attrMeta).map(([key, valSet]) => {
              const vals  = [...valSet].sort();
              const label = attrLabelMap[key] || key;
              return (
                <FormControl key={key} size="small" sx={{ minWidth: 130 }}>
                  <InputLabel sx={{ backgroundColor: '#fff', px: 0.5, fontSize: 12 }}>{label}</InputLabel>
                  <Select
                    value={attrFilters[key] || ''}
                    onChange={e => setAttrFilters(prev => ({ ...prev, [key]: e.target.value }))}
                    label={label}
                    sx={{ fontSize: 12, backgroundColor: attrFilters[key] ? 'rgba(13,29,62,0.06)' : '#fff' }}
                  >
                    <MenuItem value=""><em>הכל</em></MenuItem>
                    {vals.map(v => <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              );
            })}
          </Stack>
          {hasFilters && (
            <Typography fontSize={11} color="text.secondary" mt={1}>
              מציג {filteredVariants.filter(v => !v.archived_at).length} מתוך {variants.filter(v => !v.archived_at).length} מוצרים פעילים
            </Typography>
          )}
        </Box>
      )}

      {/* Toolbar */}
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight={700}>מוצרים בקטגוריה</Typography>
        <Tooltip title={formOpen ? 'סגור טופס' : 'הוסף מוצר'}>
          <Button
            variant={formOpen ? 'outlined' : 'contained'}
            size="small"
            startIcon={formOpen ? <CloseIcon /> : <AddIcon />}
            onClick={openAdd}
            sx={formOpen
              ? { borderColor: '#c8a96e', color: '#c8a96e' }
              : { backgroundColor: '#0d1d3e', '&:hover': { backgroundColor: '#162d5a' } }
            }
          >
            {formOpen ? 'סגור' : 'הוסף מוצר'}
          </Button>
        </Tooltip>
      </Stack>

      {/* Add form */}
      <Collapse in={formOpen} timeout={300} unmountOnExit>
        <InlineVariantForm
          productId={record.id}
          onClose={() => setFormOpen(false)}
          onSaved={handleSaved}
        />
      </Collapse>

      {/* Edit form */}
      <Collapse in={!!editingVariant} timeout={300} unmountOnExit>
        {editingVariant && (
          <InlineVariantEditForm
            key={editingVariant.id}
            variant={editingVariant}
            productId={record.id}
            onClose={() => setEditingVariant(null)}
            onSaved={handleEdited}
          />
        )}
      </Collapse>

      {/* Variants table */}
      {isLoading ? (
        <Typography color="text.secondary">טוען...</Typography>
      ) : variants.length === 0 ? (
        <Typography color="text.secondary" mt={2}>אין מוצרים עדיין — לחץ "הוסף מוצר +" להוספה</Typography>
      ) : (
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box className="crm-variants-scroll" data-mobile-scroll sx={{ overflowX: 'auto' }}><Table className="crm-variants-table" size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d1d3e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>שם</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>תכונות</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">מחיר מכירה ₪</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">מחיר מכירה $</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">מלאי</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">עריכה</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">ארכוב</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">עלויות</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">ספקים</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVariants.length === 0 && variants.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    אין מוצרים התואמים לסינון — <Button size="small" onClick={clearFilters} sx={{ color: '#c62828', fontSize: 12 }}>נקה סינון</Button>
                  </TableCell>
                </TableRow>
              ) : null}
              {filteredVariants.map((variant, i) => {
                const isEditing  = editingVariant?.id === variant.id;
                const isArchived = !!variant.archived_at;
                return (
                  <Fragment key={variant.id}>
                  <TableRow className="crm-variant-row"
                    sx={{
                      backgroundColor: isArchived ? '#f5f0e8'
                        : isEditing ? 'rgba(13,29,62,0.05)'
                        : i % 2 === 0 ? '#fff' : '#faf8f5',
                      opacity: isArchived ? 0.7 : 1,
                    }}>
                    <TableCell className="crm-variant-name" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{variant.name || '—'}</span>
                        {isArchived && (
                          <Chip label="בארכיון" size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: '#d7ccc8', color: '#5d4037' }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell className="crm-variant-attributes"><AttributeChips attrs={variant.attributes} attrDefs={attrDefs} /></TableCell>
                    <TableCell className="crm-variant-value crm-variant-ils" data-label="מחיר מכירה ₪" align="center" sx={{ fontWeight: 600 }}>
                      ₪{Number(variant.price_ils).toLocaleString()}
                    </TableCell>
                    <TableCell className="crm-variant-value crm-variant-usd" data-label="מחיר מכירה $" align="center" sx={{ fontWeight: 600 }}>
                      ${Number(variant.price_usd).toLocaleString()}
                    </TableCell>
                    <TableCell className="crm-variant-value crm-variant-stock" data-label="מלאי" align="center"><StockBadge qty={variant.quantity} unit={variant.unit} /></TableCell>
                    <TableCell className="crm-variant-action crm-variant-edit" data-label="עריכה" align="center">
                      {!isArchived && (
                        <Tooltip title={isEditing ? 'סגור עריכה' : 'ערוך'}>
                          <IconButton size="small" onClick={() => openEdit(variant)}
                            sx={{ color: isEditing ? '#c8a96e' : '#0d1d3e' }}>
                            {isEditing ? <CloseIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="crm-variant-action crm-variant-archive" data-label="ארכוב / שחזור" align="center">
                      {isArchived ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="שחזר מהארכיון">
                            <IconButton size="small" onClick={() => handleUnarchive(variant)}
                              sx={{ color: '#2e7d32' }}>
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="מחק לצמיתות">
                            <IconButton size="small" onClick={() => handleDelete(variant)}
                              sx={{ color: '#c62828' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Tooltip title="העבר לארכיון">
                          <IconButton size="small" onClick={() => handleArchive(variant)}
                            sx={{ color: '#795548', '&:hover': { backgroundColor: 'rgba(121,85,72,0.08)' } }}>
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="crm-variant-action crm-variant-costs" data-label="עלויות" align="center">
                      <Tooltip title="ניהול עלויות ייצור">
                        <IconButton size="small"
                          onClick={() => setCostsVariantId(v => v === variant.id ? null : variant.id)}
                          sx={{ color: costsVariantId === variant.id ? '#c8a96e' : '#0d1d3e', opacity: 0.7 }}>
                          <BuildIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="crm-variant-action crm-variant-suppliers" data-label="ספקים" align="center">
                      <Tooltip title="מלאי לפי ספק">
                        <IconButton size="small"
                          onClick={() => setSupplierVariantId(v => v === variant.id ? null : variant.id)}
                          sx={{ color: supplierVariantId === variant.id ? '#c8a96e' : '#0d1d3e', opacity: 0.7 }}>
                          <LocalShippingIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {/* Production costs + supplier inventory breakdown — both
                      rendered directly under THIS variant's row (not in a
                      separate loop after the table), so each stays visually
                      attached to the row that was clicked. Row is always
                      present so the Collapse close animation can play;
                      unmountOnExit keeps the panel (and its fetch) out of
                      the DOM while closed, so only the open row ever has
                      data loaded — no duplicate/background requests. */}
                  <TableRow className="crm-variant-panel">
                    <TableCell colSpan={9} sx={{ py: 0, px: 1, border: 'none' }}>
                      <Collapse in={costsVariantId === variant.id} timeout={250} unmountOnExit>
                        <Box px={1} pb={2} pt={1} sx={{ backgroundColor: '#faf8f5' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5 }}>
                            עלויות ייצור — {variant.name || `וריאנט #${variant.id}`}
                          </Typography>
                          <VariantProductionCosts variantId={variant.id} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  <TableRow className="crm-variant-panel">
                    <TableCell colSpan={9} sx={{ py: 0, px: 1, border: 'none' }}>
                      <Collapse in={supplierVariantId === variant.id} timeout={250} unmountOnExit>
                        <Box px={1} pb={2} pt={1} sx={{ backgroundColor: '#faf8f5' }}>
                          <SupplierInventoryBreakdown variantId={variant.id} unit={variant.unit} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table></Box>
        </Card>
      )}
    </Box>
  );
};  // end ProductDetailContent

const ProductShow = () => (
  <Show title=" " actions={false}>
    <ProductDetail />
  </Show>
);

export default ProductShow;
